import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Zap, Monitor, ShieldCheck, FolderOpen, Battery, Search, Wifi,
  Laptop, Apple, Lightbulb, Sparkles, Mail, CheckCircle2
} from 'lucide-react';

/**
 * Performance & Setup Guide modal — shown from Help menu.
 * Walks new users through the browser & system configuration that gets
 * AV Simulation Tool running smoothly.
 */
export default function PerformanceGuideModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="performance-guide-modal"
      >
        <DialogHeader className="sticky top-0 z-10 bg-zinc-900 -mx-6 px-6 pt-2 pb-3 border-b border-zinc-800">
          <DialogTitle className="text-zinc-100 flex items-center gap-2 text-lg">
            <Zap className="w-5 h-5 text-cyan-400" />
            Performance &amp; Setup Guide
          </DialogTitle>
          <p className="text-xs text-zinc-400 mt-1">
            Get the best experience with AV Simulation Tool
          </p>
        </DialogHeader>

        <div className="space-y-6 py-2 text-sm text-zinc-300">

          {/* Intro */}
          <Section
            title="Get the Best Experience with AV Simulation Tool"
            icon={<Sparkles className="w-4 h-4 text-cyan-400" />}
          >
            <p>
              AV Simulation Tool is designed for high-performance real-time
              visualization. A few simple settings and system checks will
              ensure everything runs smoothly.
            </p>
          </Section>

          {/* Graphics acceleration */}
          <Section
            title="Enable Graphics Acceleration"
            icon={<Zap className="w-4 h-4 text-amber-400" />}
            badge="🚀"
          >
            <p className="mb-2">For the best performance:</p>
            <Steps items={[
              'Open your browser settings',
              'Go to System',
              'Turn ON "Use hardware acceleration when available"',
              'Restart your browser',
            ]} />
            <Tip>
              This allows the app to use your GPU for smooth real-time rendering.
            </Tip>
          </Section>

          {/* Graphics support */}
          <Section
            title="Graphics Support"
            icon={<Monitor className="w-4 h-4 text-cyan-400" />}
            badge="🖥️"
          >
            <p className="mb-2">Make sure your browser supports modern graphics:</p>
            <ul className="space-y-1 ml-1">
              <Bullet>WebGL</Bullet>
              <Bullet>WebGL2</Bullet>
              <Bullet>WebGPU</Bullet>
            </ul>
            <Tip>
              These are usually enabled automatically when hardware acceleration is active.
            </Tip>
          </Section>

          {/* Site permissions */}
          <Section
            title="Allow Site Permissions"
            icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />}
            badge="🔐"
          >
            <p className="mb-2">For stable sessions and full functionality:</p>
            <ul className="space-y-1 ml-1">
              <Bullet>Cookies → Enabled</Bullet>
              <Bullet>JavaScript → Enabled</Bullet>
              <Bullet>Pop-ups → Allowed</Bullet>
            </ul>
            <Tip>
              You can manage these from the 🔒 icon in the address bar.
            </Tip>
          </Section>

          {/* File access */}
          <Section
            title="File Access &amp; Downloads"
            icon={<FolderOpen className="w-4 h-4 text-fuchsia-400" />}
            badge="📂"
          >
            <p className="mb-2">To use import/export features:</p>
            <ul className="space-y-1 ml-1">
              <Bullet>Allow file access when prompted</Bullet>
              <Bullet>Set your preferred download location</Bullet>
            </ul>
          </Section>

          {/* Keep app active */}
          <Section
            title="Keep the App Active"
            icon={<Battery className="w-4 h-4 text-amber-300" />}
            badge="⚡"
          >
            <p className="mb-2">To avoid interruptions:</p>
            <ul className="space-y-1 ml-1">
              <Bullet>Allow the app to stay active in your browser</Bullet>
              <Bullet>Avoid background tab suspension</Bullet>
            </ul>
          </Section>

          {/* Display */}
          <Section
            title="Display Settings"
            icon={<Search className="w-4 h-4 text-indigo-400" />}
            badge="🔍"
          >
            <ul className="space-y-1 ml-1">
              <Bullet>Use browser zoom at <span className="text-white font-medium">100%</span> for the best UI experience</Bullet>
            </ul>
          </Section>

          {/* Internet */}
          <Section
            title="Internet Connection"
            icon={<Wifi className="w-4 h-4 text-cyan-300" />}
            badge="🌐"
          >
            <p className="mb-2">A stable internet connection ensures smooth loading and syncing:</p>
            <ul className="space-y-1 ml-1">
              <Bullet>Recommended: High-speed broadband / Wi-Fi</Bullet>
              <Bullet>Works best on stable connections with low interruptions</Bullet>
            </ul>
            <p className="mt-3 text-xs text-zinc-400">This helps with:</p>
            <ul className="space-y-1 ml-1 mt-1">
              <Bullet>Asset loading</Bullet>
              <Bullet>Project saving</Bullet>
              <Bullet>Smooth overall experience</Bullet>
            </ul>
          </Section>

          {/* System recommendations */}
          <Section
            title="System Recommendations"
            icon={<Laptop className="w-4 h-4 text-zinc-300" />}
            badge="💻"
          >
            <p className="mb-3 text-zinc-400">
              AV Simulation Tool runs best on modern systems capable of handling real-time graphics.
            </p>

            <SubBlock icon={<Laptop className="w-3.5 h-3.5 text-cyan-300" />} title="Windows" emoji="🪟">
              <ul className="space-y-1 ml-1">
                <Bullet>Latest version of Chrome (or Chromium-based browser)</Bullet>
                <Bullet>Updated graphics drivers</Bullet>
                <Bullet>Modern processor</Bullet>
                <Bullet>Sufficient memory for handling complex scenes</Bullet>
              </ul>
            </SubBlock>

            <SubBlock icon={<Apple className="w-3.5 h-3.5 text-zinc-200" />} title="macOS" emoji="🍎">
              <ul className="space-y-1 ml-1">
                <Bullet>Latest version of Chrome / Safari (Chromium preferred)</Bullet>
                <Bullet>Apple Silicon (M1/M2/M3) or modern Intel Mac</Bullet>
                <Bullet>Updated system software</Bullet>
              </ul>
            </SubBlock>

            <Tip>
              For complex and large-scale projects, higher system capability
              will provide smoother interaction and faster performance.
            </Tip>
          </Section>

          {/* General tips */}
          <Section
            title="General Tips"
            icon={<Lightbulb className="w-4 h-4 text-yellow-300" />}
            badge="💡"
          >
            <ul className="space-y-1 ml-1">
              <Bullet>Keep your browser updated</Bullet>
              <Bullet>Close unused heavy applications while working</Bullet>
              <Bullet>Restart browser occasionally for best performance</Bullet>
            </ul>
          </Section>

          {/* Ready */}
          <Section
            title="You're Ready to Go"
            icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
            badge="🎯"
          >
            <p className="mb-2">With these settings, you'll experience:</p>
            <ul className="space-y-1 ml-1">
              <Bullet>Smooth real-time simulation</Bullet>
              <Bullet>Stable performance</Bullet>
              <Bullet>Faster workflows</Bullet>
              <Bullet>Reliable project handling</Bullet>
            </ul>
          </Section>

          {/* Need help */}
          <Section
            title="Need Help?"
            icon={<Mail className="w-4 h-4 text-cyan-400" />}
            badge="💬"
          >
            <p className="text-zinc-400">
              If you face any issues, our team is here to support you.
            </p>
          </Section>

          {/* Footer */}
          <div className="border-t border-zinc-800 pt-4 mt-2 text-center space-y-1.5">
            <div className="inline-flex items-center gap-1.5 text-cyan-300 text-xs">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Powered by Ideation Audio Visual</span>
            </div>
            <div className="text-xs text-zinc-400 flex items-center justify-center gap-1.5">
              <Mail className="w-3 h-3" />
              <a
                href="mailto:admin@ideationav.com"
                className="hover:text-cyan-300 transition-colors"
                data-testid="performance-guide-support-email"
              >
                admin@ideationav.com
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Local sub-components ──────────────────────────────────────────────

const Section = ({ title, icon, badge, children }) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
    <div className="flex items-center gap-2 mb-2">
      {badge && <span className="text-base leading-none">{badge}</span>}
      {icon}
      <h3 className="text-sm font-semibold text-white">{title}</h3>
    </div>
    <div className="text-sm text-zinc-300 leading-relaxed">{children}</div>
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-1.5 ml-1">
    {items.map((s, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-semibold shrink-0 mt-0.5">
          {i + 1}
        </span>
        <span className="text-zinc-300">{s}</span>
      </li>
    ))}
  </ol>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2 text-zinc-300">
    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0" />
    <span>{children}</span>
  </li>
);

const Tip = ({ children }) => (
  <div className="mt-3 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-100 flex items-start gap-2">
    <span className="text-base leading-none">👉</span>
    <span>{children}</span>
  </div>
);

const SubBlock = ({ icon, title, emoji, children }) => (
  <div className="mb-3 last:mb-0 pl-1">
    <div className="flex items-center gap-1.5 mb-1.5">
      {emoji && <span className="text-sm">{emoji}</span>}
      {icon}
      <span className="text-xs font-semibold text-zinc-200 uppercase tracking-wide">
        {title}
      </span>
    </div>
    {children}
  </div>
);
